import {
  Box, Card, Chip, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { usePreviousYearDues } from "../hooks/usePreviousYearDues";
import { PreviousYearDueStudent } from "../types";
import { useNotify } from "../../../context/NotificationContext";

const formatDue = (due: number) => `₹${due.toLocaleString("en-IN")}`;

export const PreviousYearDuesPage = () => {
  const { students, loading, error } = usePreviousYearDues();
  const notify = useNotify();

  // Version 1: dummy actions only — no real send request yet.
  const handleSendSms = (student: PreviousYearDueStudent) => {
    console.log("[mock] send SMS to", student.mobileNo);
    notify(`SMS queued for ${student.name} (mock)`, "info");
  };

  const handleSendWhatsApp = (student: PreviousYearDueStudent) => {
    console.log("[mock] send WhatsApp to", student.mobileNo);
    notify(`WhatsApp message queued for ${student.name} (mock)`, "info");
  };

  const totalDue = students.reduce((sum, student) => sum + student.due, 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" fontWeight={600}>
          Students with Previous Year Dues
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Outstanding dues carried over from the previous academic year.
          </Typography>
          {!loading && !error && students.length > 0 && (
            <>
              <Chip label={`${students.length} students`} size="small" variant="outlined" />
              <Chip label={`${formatDue(totalDue)} total`} size="small" variant="outlined" color="warning" />
            </>
          )}
        </Box>
      </Box>

      {loading && <Typography color="text.secondary">Loading…</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && students.length === 0 && (
        <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            No students with previous year dues.
          </Typography>
        </Card>
      )}

      {!loading && !error && students.length > 0 && (
        <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
          <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Due</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Guardian</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Mobile No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">SMS</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">WhatsApp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student, index) => (
                  <TableRow
                    key={`${student.name}-${student.mobileNo}-${index}`}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ color: "text.secondary" }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{student.name}</TableCell>
                    <TableCell>{student.grade}</TableCell>
                    <TableCell>{student.section}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#dc2626" }}>{formatDue(student.due)}</TableCell>
                    <TableCell>{student.guardian}</TableCell>
                    <TableCell>{student.mobileNo}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Send SMS">
                        <IconButton size="small" color="primary" onClick={() => handleSendSms(student)}>
                          <SmsOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Send WhatsApp message">
                        <IconButton size="small" sx={{ color: "#25D366" }} onClick={() => handleSendWhatsApp(student)}>
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};
